import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { NAVY, GOLD, TEXT_SECONDARY } from "../../theme/tokens";

export interface EmptyStateProps {
  icon?: React.ReactElement;
  title: string;
  message?: React.ReactNode;
  /** Optional CTA (usually an ActionButton). */
  action?: React.ReactNode;
  /** Compact variant for inside table bodies. */
  dense?: boolean;
}

/**
 * Friendly empty state — icon halo + title + message + optional action.
 * Replaces bare "No data" rows everywhere.
 */
export default function EmptyState({ icon, title, message, action, dense }: EmptyStateProps) {
  const glyph = icon ?? <InboxOutlinedIcon />;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: dense ? 5 : 8,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          background: alpha(NAVY, 0.06),
          border: `1px solid ${alpha(GOLD, 0.3)}`,
        }}
      >
        {React.cloneElement(glyph, { sx: { fontSize: 30, color: NAVY } })}
      </Box>
      <Typography fontSize={16} fontWeight={800} color={NAVY}>
        {title}
      </Typography>
      {message && (
        <Typography fontSize={13} color={TEXT_SECONDARY} sx={{ mt: 0.5, maxWidth: 420 }}>
          {message}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}
